import React, { useState } from "react";
import { Clock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { getBusinessDaysDiff } from "../../utils/formatters";
import { useProductionContext } from "../../contexts/ProductionContext";
import { getModelFromSku } from "../../utils/skuUtils";
import { analisarPrazo } from "../../utils/prazoEngine";
import PrazoDetalhesModal from "./PrazoDetalhesModal";

export default function DaysBadge({ date, order }) {
  const context = useProductionContext();
  const [modalOpen, setModalOpen] = useState(false);
  const days = getBusinessDaysDiff(date);

  let colorClass = "bg-emerald-100 text-emerald-700 border-emerald-200";
  let Icon = Clock;
  let statusText = "ÚTEIS";
  let tooltip = `${days} dias úteis decorridos`;

  // Se tivermos os dados do pedido e o mapa de prazos do contexto, calculamos dinamicamente pelo modelo
  if (order && context?.prazosMap) {
    const rawItems = Array.isArray(order.items) && order.items.length > 0 ? order.items : [order];

    const allAnalises = rawItems.map((it) => {
      const itemSku = it.sku || it.order?.sku || "";
      const itemModelo = getModelFromSku(itemSku);
      const itemPrazoRecord = itemModelo ? context.prazosMap[itemModelo] : null;
      const itemPrazoPrometido = itemPrazoRecord?.prazo_prometido;
      const itemFatorRisco = itemPrazoRecord?.fator_risco || 0;
      const itemIsIntercepted = Boolean(
        it.isInterceptedPE ||
        it.isIntercepted ||
        order.isInterceptedPE ||
        order.isIntercepted
      );

      return analisarPrazo({
        dataCompra:
          date ||
          it.customCreatedAt ||
          it.createdAt ||
          it.date ||
          order.customCreatedAt ||
          order.createdAt,
        statusAtual:
          (!["ATRASADO", "VAI_ATRASAR", "APERTADO", "NO_PRAZO", "EM_REVISAO", "SEM_PRAZO", "CANCELADO"].includes(String(it.etapaProducao || "").trim().toUpperCase()) && it.etapaProducao) ||
          (!["ATRASADO", "VAI_ATRASAR", "APERTADO", "NO_PRAZO", "EM_REVISAO", "SEM_PRAZO", "CANCELADO"].includes(String(it.statusAtual || "").trim().toUpperCase()) && it.statusAtual) ||
          (!["ATRASADO", "VAI_ATRASAR", "APERTADO", "NO_PRAZO", "EM_REVISAO", "SEM_PRAZO", "CANCELADO"].includes(String(it.production_step || "").trim().toUpperCase()) && it.production_step) ||
          (!["ATRASADO", "VAI_ATRASAR", "APERTADO", "NO_PRAZO", "EM_REVISAO", "SEM_PRAZO", "CANCELADO"].includes(String(it.status || "").trim().toUpperCase()) && it.status) ||
          it.logisticsStatus ||
          (!["ATRASADO", "VAI_ATRASAR", "APERTADO", "NO_PRAZO", "EM_REVISAO", "SEM_PRAZO", "CANCELADO"].includes(String(order.production_step || "").trim().toUpperCase()) && order.production_step) ||
          (!["ATRASADO", "VAI_ATRASAR", "APERTADO", "NO_PRAZO", "EM_REVISAO", "SEM_PRAZO", "CANCELADO"].includes(String(order.status || "").trim().toUpperCase()) && order.status) ||
          "SOLICITACAO",
        prazoPrometido: itemPrazoPrometido,
        temposEtapa: context.temposEtapaMap || {},
        modelo: itemModelo,
        isIntercepted: itemIsIntercepted,
        fatorRisco: itemFatorRisco,
      });
    });

    const STATUS_PRIORITY_ORDER = {
      ATRASADO: 1,
      VAI_ATRASAR: 2,
      EM_REVISAO: 3,
      APERTADO: 4,
      NO_PRAZO: 5,
      PRONTO: 6,
      ENVIADO: 7,
      SEM_PRAZO: 8,
      CANCELADO: 9,
    };

    // Ordena pela maior criticidade
    const analise = [...allAnalises].sort(
      (a, b) => (STATUS_PRIORITY_ORDER[a.status] || 99) - (STATUS_PRIORITY_ORDER[b.status] || 99)
    )[0];

    if (analise && analise.status !== "SEM_PRAZO") {
      tooltip = rawItems.length > 1
        ? `Pedido com ${rawItems.length} itens (Mais crítico: ${analise.status.replace("_", " ")}) | Prometido: ${analise.dataPrometidaFormatada} | Previsto: ${analise.dataPrevistaFormatada}`
        : `Status: ${analise.status.replace("_", " ")} | Prometido: ${analise.dataPrometidaFormatada} | Previsto: ${analise.dataPrevistaFormatada}`;

      if (analise.status === "PRONTO") {
        colorClass = "bg-emerald-100 text-emerald-800 border-emerald-300 font-extrabold";
        Icon = CheckCircle2;
        statusText = "PRONTO";
        tooltip = `Pedido pronto na oficina! Produção finalizada, aguardando envio.`;
      } else if (analise.status === "ENVIADO") {
        colorClass = "bg-blue-100 text-blue-800 border-blue-300";
        Icon = CheckCircle2;
        statusText = "ENVIADO";
        tooltip = `Pedido enviado!`;
      } else if (analise.status === "EM_REVISAO") {
        colorClass = "bg-amber-100 text-amber-800 border-amber-300";
        Icon = AlertTriangle;
        statusText = "REVISÃO";
        tooltip = `Pedido em revisão! Andamento depende do próximo status.`;
      } else if (analise.status === "ATRASADO") {
        colorClass = "bg-red-100 text-red-700 border-red-300 animate-pulse";
        Icon = AlertTriangle;
        statusText = "ATRASADO";
      } else if (analise.status === "VAI_ATRASAR") {
        colorClass = "bg-orange-100 text-orange-800 border-orange-300";
        Icon = AlertTriangle;
        statusText = "EM RISCO";
      } else if (analise.status === "APERTADO") {
        colorClass = "bg-amber-100 text-amber-800 border-amber-300";
        Icon = AlertTriangle;
        statusText = "APERTADO";
      } else {
        colorClass = "bg-emerald-100 text-emerald-700 border-emerald-200";
        Icon = Clock;
        statusText = "NO PRAZO";
      }
    } else {
      // Modelo sem prazo configurado: usa a régua padrão por dias absolutos
      if (days >= 5 && days < 8) {
        colorClass = "bg-yellow-100 text-yellow-700 border-yellow-200";
        Icon = AlertTriangle;
      } else if (days >= 8 && days <= 9) {
        colorClass = "bg-red-100 text-red-700 border-red-200 animate-pulse";
        Icon = AlertTriangle;
      } else if (days > 9) {
        colorClass = "bg-purple-900 text-white border-purple-950";
        Icon = AlertTriangle;
      }
    }
  } else {
    // Fallback padrão se order não for passado
    if (days >= 5 && days < 8) {
      colorClass = "bg-yellow-100 text-yellow-700 border-yellow-200";
      Icon = AlertTriangle;
    } else if (days >= 8 && days <= 9) {
      colorClass = "bg-red-100 text-red-700 border-red-200 animate-pulse";
      Icon = AlertTriangle;
    } else if (days > 9) {
      colorClass = "bg-purple-900 text-white border-purple-950";
      Icon = AlertTriangle;
    }
  }

  const handleClick = (e) => {
    if (order) {
      e.stopPropagation();
      setModalOpen(true);
    }
  };

  return (
    <>
      <div
        role={order ? "button" : undefined}
        tabIndex={order ? 0 : undefined}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (order && (e.key === "Enter" || e.key === " ")) {
            handleClick(e);
          }
        }}
        title={order ? `${tooltip} (Clique para abrir análise temporal detalhada)` : tooltip}
        className={`flex flex-col items-center justify-center border rounded-lg px-2 py-1 min-w-[52px] transition-all ${
          order ? "cursor-pointer hover:scale-[1.04] active:scale-[0.98] hover:shadow-xs select-none" : ""
        } ${colorClass}`}
      >
        <div className="flex items-center gap-1 text-[10px] uppercase font-bold">
          <Icon size={10} />
          <span>{days > 99 ? "+99" : days} Dias</span>
        </div>
        <span className="text-[8px] tracking-tight font-black uppercase font-mono">
          {statusText}
        </span>
      </div>

      {modalOpen && order && (
        <PrazoDetalhesModal
          order={order}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}